import React from 'react'
import styled from 'styled-components'
import breakpoint from 'src/utils/styledComponentsBreakpoint'
import Div from '@jarvis.oddle/core/components/Div'
import Card from '@jarvis.oddle/core/components/Card'
import Flex from '@jarvis.oddle/core/components/Flex'
import { H1, P, SmallText } from '@jarvis.oddle/core/components/Typo'
import {
  DefaultPromotionBannerHeader,
  DefaultPromotionBannerSubHeader,
  getPromotionAvailabilityText,
  getPromotionBanner
} from 'src/components/molecules/PromotionCard'
import Modal, { CloseButton } from '@jarvis.oddle/core/components/Modal'
import {
  borderRadius,
  borderRadiusSizes,
  colors,
  margin,
  marginSizes,
  padding,
  paddingSizes
} from '@jarvis.oddle/core/theme/default'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import { useAppContext } from 'src/containers/AppContext'
import { makeControlledModal } from 'src/decorators/ModalController'

const Container = styled(Div)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translateX(-50%) translateY(-50%);
  overflow: hidden;
  padding: 0;
  background: ${(props) => props.theme.colorContainerColor || colors.container};
  border-radius: ${borderRadius[borderRadiusSizes.medium]};
  width: 100%;
  height: 100%;
  ${breakpoint('desktop')`
    width: 500px;
    height: auto;
  `}
`

const PromotionCardContainer = styled(Card)`
  padding: 0;
  width: 100%;
  min-width: 200px;
  overflow: hidden;
  height: 100%;
  ${breakpoint('desktop')`
    padding: 0;
    height: auto;
  `}
`

const PromotionCardWrapper = styled(Flex).attrs(() => ({
  flexDirection: 'column',
  height: '100%'
}))``

const PromotionBanner = styled(Flex).attrs(() => ({
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0
}))`
  height: 180px;
  background-color: ${(props) =>
    props.theme.buttonColor || colors.brandPrimary};
  color: ${(props) => props.theme.buttonTextColor || colors.buttonTextColor};
  ${breakpoint('desktop')`
    height: 160px;
  `}
`

const PromotionContent = styled(Div).attrs(() => ({
  width: '100%'
}))`
  padding: ${padding[paddingSizes.extraSmall]} ${padding[paddingSizes.large]}
    ${padding[paddingSizes.extraSmall]} ${padding[paddingSizes.medium]};
  ${breakpoint('desktop')`
    padding: ${padding[paddingSizes.small]} ${
    padding[paddingSizes.extraLarge]
  } ${padding[paddingSizes.small]} ${padding[paddingSizes.large]};
  `}
`

const PromotionText = styled(P).attrs((props) => ({
  normalised: !props.denormalised
}))``

const PromotionSmallText = styled(SmallText)`
  padding-top: ${padding[paddingSizes.small]};
`

const StyledCloseButton = styled(CloseButton)`
  position: absolute;
  top: 30px;
  right: 10px;
  background-color: ${(props) =>
    props.theme.colorContainerColor || colors.container};
`

const PromotionHeader = styled(H1).attrs(() => ({
  bold: true
}))``

const PromotionBannerHeader = styled(DefaultPromotionBannerHeader)`
  && {
    font-size: 55px;
    line-height: 1em;
    ${breakpoint('desktop')`
      font-size: 55px;
    `}
  }
`

const PromotionBannerSubHeader = styled(DefaultPromotionBannerSubHeader)`
  && {
    font-size: 28px;
    line-height: 1em;
    margin-top: ${margin[marginSizes.small]};
    ${breakpoint('desktop')`
      font-size: 28px;
    `}
  }
`

const PromotionDetailsModal = ({
  modal,
  promotion,
  showDefaultImage
}: {
  modal: {
    show: boolean
    onClose: (handler?) => void
    onClosed: () => void
  }
  promotion: Promotion
  showDefaultImage: boolean
}): JSX.Element => {
  const { t } = React.useContext(TranslationContext)
  const { formatMoney } = useAppContext()

  const getAvailableDays = () =>
    (promotion.availableDays || '')
      .split(',')
      .filter((x) => x)
      .join(', ')

  return promotion ? (
    <Modal {...modal} slideInAnimation='bottom'>
      <Container>
        <PromotionCardContainer>
          <PromotionCardWrapper>
            {showDefaultImage && (
              <PromotionBanner>
                {getPromotionBanner({
                  t,
                  promotion,
                  formatMoney,
                  PromotionBannerHeader,
                  PromotionBannerSubHeader
                })}
              </PromotionBanner>
            )}
            <PromotionContent>
              <PromotionHeader>{promotion.name}</PromotionHeader>
              {promotion.isCustomiseStartEndDate &&
                promotion.startDate &&
                promotion.endDate && (
                  <PromotionText>
                    <span>{t('Collection Date: ')}</span>
                    <b>{`${promotion.startDate} - ${promotion.endDate}`}</b>
                  </PromotionText>
                )}
              {promotion.startTime && promotion.endTime && (
                <PromotionText>
                  <span>{t('Collection Time: ')}</span>
                  <b>{`${promotion.startTime} - ${promotion.endTime}`}</b>
                </PromotionText>
              )}
              {promotion.availableDays && (
                <PromotionText>
                  <span>{t('Available on: ')}</span>
                  <b>{`${getAvailableDays()}`}</b>
                </PromotionText>
              )}
              <PromotionText>{promotion.description}</PromotionText>
              <PromotionSmallText>
                {getPromotionAvailabilityText({ t, promotion })}
              </PromotionSmallText>
            </PromotionContent>
          </PromotionCardWrapper>
        </PromotionCardContainer>
        <StyledCloseButton onClick={modal.onClose} />
      </Container>
    </Modal>
  ) : null
}

export default makeControlledModal(
  PromotionDetailsModal,
  'PromotionDetailsModal'
)
